<template>
  <div>
    <v-dialog v-model="loadingOcrInvoiceScan" max-width="400" persistent>
      <v-card class="elevation-12">
        <v-toolbar dark color="info">
          <v-toolbar-title>
            <span> {{ $i18n.translate("Auto-filling Your Claim!") }} </span>
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="text-center pt-5">
          <p>
            {{
              $i18n.translate(
                "Please wait while we read your invoice and attempt to auto-fill your claim! It can take over a minute to read your invoice."
              )
            }}
          </p>
          <v-progress-circular indeterminate color="primary" />
          <br /><br />
          <v-btn class="primary" @click="onCancelOcrScan">
            {{ $i18n.translate("Cancel and Manually Enter Details") }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-container>
      <slot name="alert" />
      <ApiError :errors="apiErrors" v-if="apiErrors" />
      <v-row>
        <v-col cols="12" sm="12" md="9" lg="9" xl="9" class="pa-7">
          <v-form ref="uploadForm" @submit.prevent="$emit('nextStep')" v-if="!isBusy" v-model="valid.uploadForm">
            <h3 class="pb-3">Upload your Invoice</h3>
            <UploadField
              outlined
              v-model="localClaimUploads"
              :rules="[requireUpload]"
              :limitNumberFile="1"
              :public="isPublic"
              :allowedfileExtensions="['jpg', 'jpeg', 'png', 'pdf', 'tif', 'tiff']"
              :uploadOnBehalfOfParticipantId="isPublic ? null : selectedParticipant.id"
              :disabled="hasAlert"
              @change="$event != null ? showLoadingOcrInvoiceScan() : null"
              @newUpload="ocrInvoice($event)"
              @input="$refs.uploadForm.validate()"
              @uploadError="handleUploadError"
              v-if="isPublic || (selectedParticipant && localClaimUploads)"
              message="Click here to upload your invoice. "
            />
            <div class="text-center">
              <v-btn color="primary" :disabled="!valid.uploadForm || hasAlert" @click="$emit('nextStep')" class="mr-2">
                {{ $i18n.translate("Continue") }}
              </v-btn>
              <v-btn @click="$emit('cancel')" text>
                {{ $i18n.translate("Cancel") }}
              </v-btn>
            </div>
          </v-form>
        </v-col>
        <v-col cols="12" sm="12" md="3" lg="3" xl="3" class="grey lighten-3 pa-7">
          <h3 class="pb-2"><v-icon color="primary">mdi-information-outline</v-icon> Submitting Invoices</h3>
          <ul>
            <li class="pt-2 pb-2">
              Please upload your invoice, which needs to contain products and serial numbers (even if hand-written). We
              will attempt to auto-fill your claim form, but ask that you review and correct any errors, as mistakes do
              happen.
            </li>
            <li class="pt-2 pb-2">
              You will be able to upload additional supporting documentation later, but this will impact your claim's
              processing time.
            </li>
            <li class="pt-2 pb-2">
              Refer to the Promotion for proper documentation requirements and all Terms and Conditions.
              <ul>
                <li v-for="(upload, i) in uploads" :key="i">
                  <a :href="upload.href" target="_blank" rel="noreferrer">
                    {{ upload.originalFilename }}
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import UploadField from "@/gapp-components/components/fields/UploadField.vue";
import ApiError from "@/gapp-components/components/display/ApiError.vue";
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import moment from "moment-timezone";

export default {
  components: { UploadField, ApiError },
  name: "OcrStep",
  props: {
    public: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    loadingOcrInvoiceScan: false,
    isBusy: false,
    ocrAttempts: 0,
    ocrMaxAttempts: 36,
    ocrPromise: null,
    apiErrors: null,
    valid: {
      uploadForm: false
    },
    uploads: {},
    localClaimFieldValues: [],
    localClaimFields: [],
    localClaimProducts: [],
    localClaimUploads: { existing: [], deleted: [] },
    localAvailableAddresses: [],
    ocrInvoiceScanToClaimFieldMappings: [
      {
        property: "invoiceReceiptDate",
        claimFieldTypeName: "INVOICE_DATE_TYPE",
        convert: v => moment.tz(v.split("T")[0], "YYYY-MM-DD", "America/New_York").format()
      },
      {
        property: "invoiceReceiptId",
        claimFieldTypeName: "INVOICE_NUMBER_TYPE",
        convert: v => v
      },
      { property: "total", claimFieldTypeName: "INVOICE_AMOUNT_TYPE", convert: v => v.toString() },
      {
        property: "receiverFirstName",
        claimFieldTypeName: "END_USER_FIRST_NAME_TYPE",
        convert: v => v
      },
      {
        property: "receiverLastName",
        claimFieldTypeName: "END_USER_LAST_NAME_TYPE",
        convert: v => v
      },
      {
        property: "receiverOrganizationName",
        claimFieldTypeName: "END_USER_ORGANIZATION_NAME_TYPE",
        convert: v => v
      },
      {
        property: "receiverPhone",
        claimFieldTypeName: "END_USER_PHONE_NUMBER1_TYPE",
        convert: v => v
      }
    ],
    addressToClaimFieldMappings: [
      {
        addressField: "address1",
        claimFieldTypeName: "END_USER_ADDRESS1_TYPE",
        convert: v => v
      },
      {
        addressField: "address2",
        claimFieldTypeName: "END_USER_ADDRESS2_TYPE",
        convert: v => v
      },
      {
        addressField: "city",
        claimFieldTypeName: "END_USER_CITY_TYPE",
        convert: v => v
      },
      {
        addressField: "region",
        claimFieldTypeName: "END_USER_REGION_TYPE",
        convert: v => v
      },
      {
        addressField: "country",
        claimFieldTypeName: "END_USER_COUNTRY_TYPE",
        convert: v => v
      },
      {
        addressField: "postalCode",
        claimFieldTypeName: "END_USER_POSTAL_CODE_TYPE",
        convert: v => v
      }
    ],
    ocrDocumentQueryToClaimFieldMappings: [
      {
        query: "What is the customer first name?",
        claimFieldTypeName: "END_USER_FIRST_NAME_TYPE",
        convert: v => v
      },
      {
        query: "What is the customer last name?",
        claimFieldTypeName: "END_USER_LAST_NAME_TYPE",
        convert: v => v
      },
      {
        query: "What is the customer company?",
        claimFieldTypeName: "END_USER_ORGANIZATION_NAME_TYPE",
        convert: v => v
      },
      {
        query: "What is the ship to address?",
        claimFieldTypeName: "END_USER_ADDRESS1_TYPE",
        convert: v => v
      },
      {
        query: "What is the ship to city?",
        claimFieldTypeName: "END_USER_CITY_TYPE",
        convert: v => v
      },
      {
        query: "What is the ship to state?",
        claimFieldTypeName: "END_USER_REGION_TYPE",
        convert: v => v
      },
      {
        query: "What is the ship to country?",
        claimFieldTypeName: "END_USER_COUNTRY_TYPE",
        convert: v => v
      },
      {
        query: "What is the ship to zip code?",
        claimFieldTypeName: "END_USER_POSTAL_CODE_TYPE",
        convert: v => v
      },
      {
        query: "What is the customer email?",
        claimFieldTypeName: "END_USER_EMAIL_TYPE",
        convert: v => v
      },
      {
        query: "What is the customer phone number?",
        claimFieldTypeName: "END_USER_PHONE_NUMBER1_TYPE",
        convert: v => v
      }
    ]
  }),
  computed: {
    ...mapGetters([
      "selectedParticipant",
      "claimFields",
      "claimUploads",
      "claimFieldValues",
      "claimProducts",
      "availableAddresses",
      "promotion",
      "claim",
      "selectedClient",
      "selectedProgram"
    ]),
    isPublic() {
      return this.public;
    },
    hasAlert() {
      return this.$slots.alert?.length > 0;
    }
  },
  watch: {
    valid: {
      deep: true,
      immediate: true,
      handler(v) {
        this.$emit("validation", v.uploadForm);
      }
    },
    claimFields: {
      deep: true,
      immediate: true,
      handler(v) {
        this.localClaimFields = v;
      }
    },
    claimFieldValues: {
      deep: true,
      immediate: true,
      handler(v) {
        this.localClaimFieldValues = v;
      }
    },
    claimProducts: {
      deep: true,
      immediate: true,
      handler(v) {
        this.localClaimProducts = v;
      }
    },
    claimUploads: {
      deep: true,
      immediate: true,
      handler(v) {
        this.localClaimUploads = v;
      }
    },
    availableAddresses: {
      deep: true,
      immediate: true,
      handler(v) {
        this.localAvailableAddresses = v;
      }
    },
    localClaimFields: {
      deep: true,
      immediate: true,
      handler(v) {
        this.updateClaimFields(v);
      }
    },
    localClaimFieldValues: {
      deep: true,
      immediate: true,
      handler(v) {
        this.updateClaimFieldValues(v);
      }
    },
    localClaimProducts: {
      deep: true,
      immediate: false,
      handler(v) {
        this.updateClaimProducts(v);
      }
    },
    localClaimUploads: {
      deep: true,
      immediate: false,
      handler(v) {
        this.updateClaimUploads(v);
      }
    },
    localAvailableAddresses: {
      deep: true,
      immediate: true,
      handler(v) {
        this.updateAvailableAddresses(v);
      }
    }
  },
  methods: {
    ...mapActions([
      "updateClaimFieldValues",
      "updateClaimFields",
      "updateClaimUploads",
      "updateClaimProducts",
      "updateAvailableAddresses"
    ]),
    requireUpload() {
      if (this.claimUploads && this.claimUploads && this.claimUploads.existing) {
        if (this.claimUploads.existing.length > 0) {
          return true;
        } else {
          return "At least 1 upload is required";
        }
      } else {
        return false;
      }
    },
    showLoadingOcrInvoiceScan() {
      this.loadingOcrInvoiceScan = true;
      this.$forceUpdate();
    },
    ocrInvoice(upload) {
      if (this.$ocr.isOcrScanCancelled()) {
        return;
      }

      this.apiErrors = null;
      this.showLoadingOcrInvoiceScan();

      let ocrPromise = this.$ocr.processInvoiceAndGetResult(upload, ["S01"], []);

      this.ocrPromise = ocrPromise
        .then(async ocrResult => {
          if (this.$ocr.isOcrScanCancelled()) {
            return Promise.reject("OCR cancelled");
          }
          await this.processNewOcrInvoiceResult(ocrResult);
          this.$root.$emit("ClaimFieldsProductsDocumentationEntryStep-validate");

          this.localAvailableAddresses = [];
          this.loadingOcrInvoiceScan = false;
        })
        .then(() => {
          this.$root.$emit("ClaimCompleteInformationEntryStep-validate");
          this.$emit("nextStep");
        })
        .catch(error => {
          console.log(error);
          let message = this.$i18n.translate(
            "Automatic loading of the information in your invoice only works with PDF, JPG, JPEG, PNG, TIF and TIFF invoice files."
          );
          if (!error || !error.response || error.response.status !== 400) {
            message =
              error && error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : message;
          }
          this.apiErrors = {
            message: message
          };
          this.loadingOcrInvoiceScan = false;
        });
    },

    onCancelOcrScan() {
      this.$ocr.cancelOcrScan();
      this.loadingOcrInvoiceScan = false;
      this.$emit("nextStep");
    },

    handleUploadError(errorMessage) {
      this.loadingOcrInvoiceScan = false;
      this.localClaimUploads.existing = [];
      if (errorMessage === "Invalid file type") {
        this.apiErrors = {
          message: this.$i18n.translate("Automatic loading of the information in your invoice only works with PDF, JPG, JPEG, PNG, TIF and TIFF invoice files.")
        };
      } else {
        this.apiErrors = { message: errorMessage };
      }
      this.$forceUpdate();
      this.$emit("error", errorMessage);
    },

    loadAddress(address, initialLoad) {
      this.addressToClaimFieldMappings.forEach(mapping => {
        let claimField = this.findClaimFieldByClaimFieldType(this.localClaimFields, mapping.claimFieldTypeName);
        if (claimField) {
          this.$set(this.localClaimFieldValues, claimField.id, mapping.convert(address[mapping.addressField]));
          if (initialLoad) {
            claimField.ocrPopulated = true;
          }
        }
      });
      this.$forceUpdate();
    },
    findClaimFieldByClaimFieldType(claimFields, claimFieldTypeName) {
      let returnValue = null;
      claimFields.forEach(cf => {
        if (cf.claimFieldType.name == claimFieldTypeName) {
          returnValue = cf;
        }
      });
      return returnValue;
    },
    processOcrDocumentQueryResult(ocrResult, upload) {
      if (ocrResult.status == "READY" && this.ocrAttempts < this.ocrMaxAttempts) {
        let claimField;
        for (let answer in ocrResult.answers) {
          let answersList = ocrResult.answers[answer];
          let queryMapping = this.ocrDocumentQueryToClaimFieldMappings.find(elem => elem.query == answer);
          if (queryMapping) {
            claimField = this.findClaimFieldByClaimFieldType(queryMapping.claimFieldTypeName);
            if (claimField) {
              this.$set(this.localClaimFieldValues, claimField.id, queryMapping.convert(answersList[0]));
              this.$set(claimField, "ocrPopulated", true);
            }
          }
        }
        this.loadingOcrDocumentQuery = false;
      } else if (ocrResult.status == "PENDING" && this.ocrAttempts < this.ocrMaxAttempts) {
        this.ocrAttempts++;
        setTimeout(() => {
          this.$api.post("/api/ocr/checkDocumentQuery/" + this.promotion.id + "/" + upload.id).then(({ data }) => {
            this.processOcrDocumentQueryResult(data, upload);
          });
        }, 5000);
      } else if (this.ocrAttempts == this.ocrMaxAttempts) {
        this.loadingOcrDocumentQuery = false;
      }

      this.$forceUpdate();
    },
    async processNewOcrInvoiceResult(ocrResult) {
      if (!ocrResult || !ocrResult.suggested) return;

      let products = this.getOcrProducts(ocrResult);
      if (products && products.length > 0) {
        let addedProducts = [];
        products.forEach(product => {
          let promotionProduct = this.findPromotionProduct(product);
          if (!promotionProduct) {
            return;
          }
          let productAlreadyAdded = addedProducts.find(addedProduct => {
            return addedProduct.id === product.id && addedProduct.fieldIndex === product.fieldIndex;
          });
          if (!productAlreadyAdded) {
            let serialNumberProduct = products.find(suggestedProduct => {
              return (
                suggestedProduct.id === product.id &&
                suggestedProduct.serialNumbers &&
                suggestedProduct.serialNumbers.length > 0
              );
            });
            let productWithSerialNumber = serialNumberProduct ? serialNumberProduct : product;
            let serialNumbers = this.getSerialNumbersFromSuggestedProduct(productWithSerialNumber);
            let requiresSerialNumber = Boolean(promotionProduct.serialNumberRequired);

            if (requiresSerialNumber && serialNumbers.length > 0) {
              serialNumbers.forEach(serialNumber => {
                this.localClaimProducts.push({
                  promotionProduct: promotionProduct,
                  quantity: 1,
                  serialNumber: serialNumber,
                  ocrPopulated: true
                });
              });
            } else {
              this.localClaimProducts.push({
                promotionProduct: promotionProduct,
                quantity: product.quantity ? parseInt(product.quantity) || 1 : 1,
                serialNumber: requiresSerialNumber ? null : this.getSerialNumberFromSuggestedProduct(productWithSerialNumber),
                ocrPopulated: true
              });
            }
            addedProducts.push({
              id: product.id,
              fieldIndex: product.fieldIndex
            });
          }
        });
      }

      let invoice = ocrResult.suggested.invoice;
      if (invoice) {
        this.setOcrClaimFieldValue(
          "INVOICE_DATE_TYPE",
          this.formatNewOcrDate(this.getSuggestedDateValue(invoice.invoiceDate))
        );
        this.setOcrClaimFieldValue("INVOICE_NUMBER_TYPE", this.getSuggestedValue(invoice.invoiceNumber));
        this.setOcrClaimFieldValue(
          "INVOICE_AMOUNT_TYPE",
          this.formatNewOcrAmount(this.getSuggestedValue(invoice.totalAmount))
        );
      }

      let receiver = ocrResult.suggested.receiver;
      if (receiver) {
        let receiverName = this.getSuggestedValue(receiver.name);
        if (receiverName) {
          let receiverContactNameField = (ocrResult.fields || []).find(field => {
            return (
              field.label &&
              field.label.text === "RECEIVER_NAME" &&
              field.value &&
              field.value.text &&
              field.value.text.trim() !== receiverName
            );
          });
          let receiverContactName = receiverContactNameField
            ? receiverContactNameField.value.text.trim()
            : receiverName;
          let nameParts = receiverContactName.split(" ");
          let firstName = nameParts.shift();
          let lastName = nameParts.join(" ");
          this.setOcrClaimFieldValue("END_USER_FIRST_NAME_TYPE", firstName);
          this.setOcrClaimFieldValue("END_USER_LAST_NAME_TYPE", lastName);
          if (receiverContactName !== receiverName) {
            this.setOcrClaimFieldValue("END_USER_ORGANIZATION_NAME_TYPE", receiverName);
          }
        }

        this.setOcrClaimFieldValue("END_USER_PHONE_NUMBER1_TYPE", this.getSuggestedValue(receiver.phone));
        this.setOcrClaimFieldValue("END_USER_EMAIL_TYPE", this.getSuggestedValue(receiver.email));

        this.setOcrClaimFieldValue("END_USER_ADDRESS1_TYPE", this.getSuggestedValue(receiver.address1));
        this.setOcrClaimFieldValue("END_USER_ADDRESS2_TYPE", this.getSuggestedValue(receiver.address2));
        this.setOcrClaimFieldValue("END_USER_CITY_TYPE", this.getSuggestedValue(receiver.city));
        this.setOcrClaimFieldValue("END_USER_POSTAL_CODE_TYPE", this.getSuggestedValue(receiver.postalCode));
        let country = this.getOcrCountry(receiver.country);
        let region = await this.getOcrRegion(receiver.region, country);
        this.setOcrClaimFieldValue("END_USER_REGION_TYPE", region);
        this.setOcrClaimFieldValue("END_USER_COUNTRY_TYPE", country);
      }

      await this.processOcrVendorOrganization(ocrResult.suggested.vendor);

      this.$forceUpdate();
    },
    async processOcrVendorOrganization(vendor) {
      let vendorName = this.getSuggestedValue(vendor ? vendor.name : null);
      let claimField = this.findOcrVendorOrganizationClaimField();
      if (!vendorName || !claimField) return;

      let organizations = await this.searchOcrVendorOrganizations(vendorName, claimField);
      let exactOrganizations = organizations.filter(organization => {
        return this.cleanOcrOrganizationName(organization.name) === this.cleanOcrOrganizationName(vendorName);
      });

      if (exactOrganizations.length === 1) {
        Vue.set(this.localClaimFieldValues, claimField.id, exactOrganizations[0]);
        Vue.set(claimField, "ocrPopulated", true);
        Vue.set(claimField, "ocrSuggestedSearchText", null);
      } else {
        Vue.set(claimField, "ocrSuggestedSearchText", vendorName);
      }
    },
    findOcrVendorOrganizationClaimField() {
      return this.localClaimFields.find(claimField => {
        return (
          claimField.claimFieldType &&
          claimField.claimFieldType.name === "ORGANIZATION" &&
          claimField.name &&
          (claimField.name.trim() === "Sony Dealer" || claimField.name.trim() === "Reseller")
        );
      });
    },
    async searchOcrVendorOrganizations(vendorName, claimField) {
      let searchForm = claimField.searchFilterJson ? JSON.parse(claimField.searchFilterJson) : {};
      let url = "/api/organizations/search?sort=name,ASC";

      if (this.isPublic) {
        url = "/api/organizations/search/public?sort=name,ASC";
        if (this.selectedProgram && !searchForm.programKey && !searchForm.programGroupKey) {
          searchForm.programKey = this.selectedProgram.programKey;
          searchForm.programGroupKey = this.selectedProgram.programGroup.programGroupKey;
        }
      }

      searchForm.keyword = vendorName;

      let response = await this.$api.post(url, searchForm);
      return response.data && response.data.content ? response.data.content : [];
    },
    cleanOcrOrganizationName(value) {
      if (!value) return "";
      return value
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    },
    setOcrClaimFieldValue(claimFieldTypeName, value) {
      if (!value) return;

      let claimField = this.findClaimFieldByClaimFieldType(this.localClaimFields, claimFieldTypeName);
      if (claimField) {
        Vue.set(this.localClaimFieldValues, claimField.id, value);
        Vue.set(claimField, "ocrPopulated", true);
      }
    },
    getSuggestedValue(suggestedFieldElement) {
      if (
        suggestedFieldElement &&
        suggestedFieldElement.cleanedValue &&
        suggestedFieldElement.cleanedValue.toString().trim().length > 0
      ) {
        return suggestedFieldElement.cleanedValue.toString().trim();
      }
      return null;
    },
    getSuggestedDateValue(suggestedFieldElement) {
      if (
        suggestedFieldElement &&
        suggestedFieldElement.isoDateValue &&
        suggestedFieldElement.isoDateValue.toString().trim().length > 0
      ) {
        return suggestedFieldElement.isoDateValue.toString().trim();
      }
      console.warn("OCR invoice date has no ISO value", {
        suggestedFieldElement: suggestedFieldElement
      });
      return null;
    },
    getOcrCountry(suggestedFieldElement) {
      let value = this.getSuggestedValue(suggestedFieldElement);
      if (!this.promotion || !this.promotion.country) {
        return null;
      }

      let country = this.promotion.country;
      if (!value) {
        return country;
      }

      let normalizedValue = value.trim().toLowerCase();
      if (
        (country.name && country.name.toLowerCase() === normalizedValue) ||
        (country.description && country.description.toLowerCase() === normalizedValue) ||
        (country.isoAlpha2 && country.isoAlpha2.toLowerCase() === normalizedValue) ||
        (country.isoAlpha3 && country.isoAlpha3.toLowerCase() === normalizedValue) ||
        (country.localeCountry && country.localeCountry.toLowerCase() === normalizedValue)
      ) {
        return country;
      }
      return null;
    },
    async getOcrRegion(suggestedFieldElement, country) {
      let value = this.getSuggestedValue(suggestedFieldElement);
      if (!value || !country || !country.name) {
        return null;
      }

      let response = await this.$api.get("/api/types/address/regions/country/" + country.name);
      let normalizedValue = value.trim().toLowerCase();
      return response.data.find(region => {
        return (
          (region.name && region.name.toLowerCase() === normalizedValue) ||
          (region.isoAlpha2 && region.isoAlpha2.toLowerCase() === normalizedValue) ||
          (region.description && region.description.toLowerCase() === normalizedValue)
        );
      });
    },
    findPromotionProduct(product) {
      if (!this.promotion || !this.promotion.promotionProducts) return null;

      return this.promotion.promotionProducts.find(promotionProduct => {
        return (
          (product.id && promotionProduct.product && promotionProduct.product.id === product.id) ||
          (product.productKey &&
            promotionProduct.product &&
            promotionProduct.product.productKey === product.productKey)
        );
      });
    },
    getOcrProducts(ocrResult) {
      let suggestedProducts = ocrResult && ocrResult.suggested ? ocrResult.suggested.products || [] : [];
      let productsByFieldIndex = {};
      let productsByProductKey = {};

      suggestedProducts.forEach(product => {
        if (product.fieldIndex !== undefined) {
          productsByFieldIndex[product.fieldIndex] = product;
        }
        if (product.productKey) {
          productsByProductKey[product.productKey] = product;
        }
      });

      if (ocrResult && Array.isArray(ocrResult.lineItemFields) && ocrResult.lineItemFields.length > 0) {
        return ocrResult.lineItemFields
          .filter(lineItem => lineItem.productCode && lineItem.productCode.text)
          .map(lineItem => {
            let productKey = lineItem.productCode.text.trim();
            let product = productsByFieldIndex[lineItem.fieldIndex] || productsByProductKey[productKey] || {};

            return {
              id: product.id,
              productKey: productKey,
              name: product.name,
              fieldIndex: lineItem.fieldIndex,
              matchedText: product.matchedText || productKey,
              matchType: product.matchType,
              confidence: product.confidence,
              serialNumbers: product.serialNumbers || [],
              quantity: lineItem.quantity && lineItem.quantity.text ? lineItem.quantity.text : 1
            };
          });
      }

      return suggestedProducts;
    },
    getSerialNumbersFromSuggestedProduct(product) {
      if (product.serialNumbers && product.serialNumbers.length > 0) {
        return product.serialNumbers
          .map(serialNumber => this.serialNumberConversion(serialNumber.productSerialNumberKey))
          .filter(serialNumber => serialNumber);
      }
      if (product.matchType && product.matchType.indexOf("SERIAL_NUMBER") > -1) {
        let serialNumber = this.serialNumberConversion(product.matchedText);
        return serialNumber ? [serialNumber] : [];
      }
      return [];
    },
    getSerialNumberFromSuggestedProduct(product) {
      let serialNumbers = this.getSerialNumbersFromSuggestedProduct(product);
      return serialNumbers.length > 0 ? serialNumbers[0] : null;
    },
    formatNewOcrDate(value) {
      if (!value) return null;

      let dateValue = value.split("T")[0];
      let timezone =
        this.selectedClient && this.selectedClient.timezone ? this.selectedClient.timezone : moment.tz.guess();
      let date = moment.tz(dateValue, ["YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"], true, timezone);
      if (date.isValid()) {
        return date.format();
      }
      console.warn("OCR invoice date has no supported date format", {
        value: value,
        timezone: timezone
      });
      return value;
    },
    formatNewOcrAmount(value) {
      if (!value) return null;

      return value.toString().replace(/[^0-9.]/g, "");
    },
    processOcrInvoiceScanResult(ocrResult) {
      if (!ocrResult) return;
      for (let productLine of ocrResult.productLines) {
        this.localClaimProducts.push({
          promotionProduct: this.promotion.promotionProducts.find(
            pp => pp.product.productKey == productLine.productKey
          ),
          quantity: productLine.quantity ? productLine.quantity : 1,
          serialNumber: this.serialNumberConversion(productLine.serialNumber),
          ocrPopulated: true
        });
      }
      this.$forceUpdate();

      this.ocrInvoiceScanToClaimFieldMappings.forEach(mapping => {
        let claimField = this.findClaimFieldByClaimFieldType(this.localClaimFields, mapping.claimFieldTypeName);
        if (ocrResult[mapping.property] && claimField) {
          Vue.set(this.localClaimFieldValues, claimField.id, mapping.convert(ocrResult[mapping.property]));
          Vue.set(claimField, "ocrPopulated", true);
        }
      });
    },
    serialNumberConversion: serialNumber => {
      if (!serialNumber) return;
      let s01removed = serialNumber.replace(/^S01/gi, "");
      let cleanSerialNumber = s01removed.substring(0, Math.min(7, s01removed.length));
      return cleanSerialNumber;
    }
  }
};
</script>

<style></style>
